import { AttachInternals, Component, Element, h, Host, Prop } from '@stencil/core'

import Helpers from '../../utils/helpers'
import { CssClassMap } from '../../utils/interfaces'

/**
 * ## Design System
 *
 * Para a documentação completa de design, incluindo diretrizes de uso, acessibilidade e exemplos visuais, consulte o [Design System do GovBR](https://www.gov.br/ds/components/card?tab=designer).
 *
 * @slot default - Slot para conteúdo livre do card, quando não usar a estrutura de slots nomeados.
 * @slot header - Slot para o conteúdo do cabeçalho do card.
 * @slot content - Slot para o corpo principal do card ao usar a estrutura de slots.
 * @slot footer - Slot para o conteúdo do rodapé do card.
 */
@Component({
  tag: 'br-card',
  styleUrl: 'card.scss',
  shadow: true,
  formAssociated: true,
})
export class BrCard {
  /**
   * Referência ao elemento host do componente.
   * Utilize esta propriedade para acessar e manipular o elemento do DOM associado ao componente.
   */
  @Element() el: HTMLBrCardElement

  @AttachInternals() elementInternals: ElementInternals

  /**
   * Identificador único; gerado automaticamente quando omitido.
   */
  @Prop({ reflect: true }) readonly customId: string = Helpers.generateUniqueId('br-card')

  /**
   * Define se o card está desabilitado.
   * Quando true, o card fica com opacidade reduzida e não responde a interações.
   */
  @Prop({ reflect: true }) disabled: boolean = false

  /**
   * Define se o card tem hover interativo.
   * Quando true, o card terá efeitos visuais ao passar o mouse.
   */
  @Prop({ reflect: true }) hover: boolean = false

  private hasHeader: boolean = false
  private hasContent: boolean = false
  private hasFooter: boolean = false

  componentWillLoad() {
    this.checkSlots()
  }

  componentDidLoad() {
    // Observar mudanças nos slots
    const slotElements = this.el.shadowRoot?.querySelectorAll('slot')
    slotElements?.forEach((slot) => {
      slot.addEventListener('slotchange', () => this.checkSlots())
    })
  }

  private checkSlots() {
    const headerSlot = this.el.querySelector('[slot="header"]')
    const contentSlot = this.el.querySelector('[slot="content"]')
    const footerSlot = this.el.querySelector('[slot="footer"]')

    this.hasHeader = !!headerSlot
    this.hasContent = !!contentSlot
    this.hasFooter = !!footerSlot
  }

  private getCssClassMap(): CssClassMap {
    return {
      'br-card': true,
      disabled: this.disabled,
      hover: this.hover && !this.disabled,
    }
  }

  private getHeaderClassMap(): CssClassMap {
    return {
      'card-header': true,
    }
  }

  private getContentClassMap(): CssClassMap {
    return {
      'card-content': true,
    }
  }

  private getFooterClassMap(): CssClassMap {
    return {
      'card-footer': true,
    }
  }

  render() {
    const hasStructuredContent = this.hasHeader || this.hasContent || this.hasFooter

    return (
      <Host id={this.customId} aria-disabled={this.disabled ? 'true' : null}>
        <div class={this.getCssClassMap()} part="wrapper">
          {hasStructuredContent ? (
            [
              this.hasHeader && (
                <div class={this.getHeaderClassMap()} part="header">
                  <slot name="header" />
                </div>
              ),
              (this.hasContent || (!this.hasHeader && !this.hasFooter)) && (
                <div class={this.getContentClassMap()} part="content">
                  <slot name="content">
                    <slot />
                  </slot>
                </div>
              ),
              this.hasFooter && (
                <div class={this.getFooterClassMap()} part="footer">
                  <slot name="footer" />
                </div>
              ),
            ]
          ) : (
            <div class={this.getContentClassMap()} part="content">
              <slot />
            </div>
          )}
        </div>
      </Host>
    )
  }
}
